import { useMemo } from "react";
import { AxiosError } from "axios";
import { StatisticalAnalysisResponse } from "@/services/statistical-analysis-service";
import { AlertType } from "@/utils/alert-styles";
import { checkSampleSize, DEFAULT_MIN_SAMPLE_SIZE } from "../utils";

export interface PriceFieldState {
  showAlert: boolean;
  alertType: AlertType;
  alertTitle: string;
  alertMessage: string;
  value: string;
}

export interface CapacityFieldState {
  showAlert: boolean;
  alertType: AlertType;
  alertTitle: string;
  alertMessage: string;
}

export interface UsePriceFieldAlertParams {
  monthlyPrice?: string;
  statisticalData?: StatisticalAnalysisResponse;
  statisticalError?: AxiosError | null;
  isLoading?: boolean;
}

export interface UseCapacityFieldAlertParams {
  size?: string;
  capacity?: string;
  statisticalData?: StatisticalAnalysisResponse;
  localSizeCapacityRatio?: {
    ratio: number;
    isWithinStandard: boolean;
    status: string;
    warning: string | null;
  } | null;
}

export const usePriceFieldAlert = ({
  monthlyPrice,
  statisticalData,
  statisticalError,
  isLoading: explicitLoading = false,
}: UsePriceFieldAlertParams): PriceFieldState => {
  return useMemo(() => {
    const hasPrice = monthlyPrice && monthlyPrice.trim() !== "";
    const isLoading =
      explicitLoading && hasPrice && !statisticalData && !statisticalError;
    const hasPriceData = statisticalData?.analyses?.monthlyPrice;
    const sampleSize = statisticalData?.sampleSize;

    const value = hasPrice ? `R$ ${monthlyPrice}` : "Não informado";

    if (!hasPrice) {
      return {
        showAlert: true,
        alertType: "loading",
        alertTitle: "Aguardando análise",
        alertMessage: "Preço não informado",
        value,
      };
    }

    if (statisticalError && hasPrice) {
      return {
        showAlert: true,
        alertType: "error",
        alertTitle: "Erro na análise",
        alertMessage:
          "Não foi possível analisar o preço no momento. Tente novamente em alguns minutos.",
        value,
      };
    }

    if (isLoading) {
      return {
        showAlert: true,
        alertType: "loading",
        alertTitle: "Aguardando análise",
        alertMessage: "Calculando análise do preço...",
        value,
      };
    }

    const sampleSizeCheck = checkSampleSize(
      sampleSize,
      DEFAULT_MIN_SAMPLE_SIZE,
    );

    if (sampleSizeCheck.hasInsufficientSample) {
      return {
        showAlert: true,
        alertType: "warning",
        alertTitle: "Dados insuficientes para análise",
        alertMessage: sampleSizeCheck.warningMessage,
        value,
      };
    }

    if (hasPriceData) {
      const analysis = statisticalData.analyses.monthlyPrice;
      const isWithinRange = analysis.outlierDetection.status === "within_range";

      if (isWithinRange) {
        return {
          showAlert: true,
          alertType: "success",
          alertTitle: "Valor dentro da média",
          alertMessage:
            "Valor dentro do padrão para salas semelhantes na região.",
          value,
        };
      } else {
        const isHigh = analysis.outlierDetection.status === "above_outlier";
        const title = `Valor ${isHigh ? "acima" : "abaixo"} da média`;

        // Guard against undefined recommendations
        const hasValidRecommendations =
          typeof analysis.recommendations?.suggestedMin === "number" &&
          typeof analysis.recommendations?.suggestedMax === "number";

        const message = hasValidRecommendations
          ? `Recomendamos revisar o preço. Faixa de referência: ${analysis.recommendations!.suggestedMin.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} - ${analysis.recommendations!.suggestedMax.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
          : analysis.outlierDetection.message ||
            "Recomendamos revisar o preço para este bairro.";

        return {
          showAlert: true,
          alertType: "error",
          alertTitle: title,
          alertMessage: message,
          value,
        };
      }
    }

    return {
      showAlert: false,
      alertType: "info",
      alertTitle: "",
      alertMessage: "",
      value,
    };
  }, [monthlyPrice, statisticalData, statisticalError, explicitLoading]);
};

export const useCapacityFieldAlert = ({
  size,
  capacity,
  statisticalData,
  localSizeCapacityRatio,
}: UseCapacityFieldAlertParams): CapacityFieldState => {
  return useMemo(() => {
    const hasSize = size && size.trim() !== "";
    const hasCapacity = capacity && capacity.trim() !== "";
    const hasCompleteData = hasSize && hasCapacity;

    const backendRatioData = statisticalData?.spaceRatioValidation;
    const isLoading =
      !backendRatioData && !localSizeCapacityRatio && hasCompleteData;

    if (!hasCompleteData) {
      return {
        showAlert: true,
        alertType: "loading",
        alertTitle: "Aguardando análise",
        alertMessage: "Dados incompletos para análise",
      };
    }

    if (isLoading) {
      return {
        showAlert: true,
        alertType: "loading",
        alertTitle: "Aguardando análise",
        alertMessage: "Calculando proporção do espaço...",
      };
    }

    const ratioValidation = backendRatioData || localSizeCapacityRatio;

    if (ratioValidation) {
      const isWithinStandard = ratioValidation.isWithinStandard;

      if (isWithinStandard) {
        return {
          showAlert: true,
          alertType: "success",
          alertTitle: "Proporção adequada",
          alertMessage: `Boa proporção: cerca de ${ratioValidation.ratio.toFixed(1)} m² por pessoa. O espaço está dentro do ideal para conforto e uso.`,
        };
      } else {
        const isHigh = ratioValidation.status === "below_minimum";
        const title = `Espaço ${isHigh ? "abaixo" : "acima"} do ideal`;
        const message =
          ratioValidation.warning ||
          `Proporção inadequada: cerca de ${ratioValidation.ratio.toFixed(1)} m² por pessoa. Ajuste o tamanho ou a capacidade para garantir conforto dos clientes.`;

        return {
          showAlert: true,
          alertType: "error",
          alertTitle: title,
          alertMessage: message,
        };
      }
    }

    return {
      showAlert: false,
      alertType: "info",
      alertTitle: "",
      alertMessage: "",
    };
  }, [size, capacity, statisticalData, localSizeCapacityRatio]);
};
